import React, {useState, useEffect} from "react";
import "./style.scss";
import SelectDropDown from "../ui/SelectDropdown";
import Button from "../Button";
import {useDispatch, useSelector} from "react-redux";
import {setFilter, setFilterLoader} from "../../store/filterSlice";
import {useGetSites} from "../../queries/useGetSites";
import {useGetCustomerItems} from "../../queries/useGetCustomerItems";
import Select from "react-select";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import dayjs, {Dayjs} from "dayjs";
import {useGetAllDetailsQuery} from "../../queries/PassPlan";
import {RootState} from "../../store/store";
import {useLocation} from "react-router-dom";

interface SelectedValues {
	location: number[];
	startDate: string;
	endDate: string;
	plan: string[];
	dimension: string;
	loader: boolean;
}

const Header: React.FC = () => {
	const dispatch = useDispatch();
	const selectedBrand = useSelector(
		(state: RootState) => state.brand.selectedBrand
	);
	const selectedFilter = useSelector((state: RootState) => state.filter);
	const location = useLocation();

	const isTicketingPage = location.pathname === "/ticketing";
	const isEmployeeLeaderboards = location.pathname === "/employee-leaderboards";
	const isSiteLeaderboards = location.pathname === "/site-leaderboards";
	const isRegionalTargets = location.pathname === "/regional-targets";
	const checkPagesTogether = (isTicketingPage || isEmployeeLeaderboards || isSiteLeaderboards || isRegionalTargets);

	const [selectedValues, setSelectedValues] = useState<SelectedValues>({
		...selectedFilter,
	});

	const {data: locationData, isFetching, isError} = useGetSites();
	const {
		data: plansData,
		isFetching: isFetchingPlan,
		isError: errorPlan,
	} = useGetAllDetailsQuery(selectedBrand);
	const {
		data: customerItemData,
		isFetching: isFetchingCustomerItem,
		isError: errorCustomerItem,
	} = useGetCustomerItems();

	useEffect(() => {
		if (
			plansData &&
			plansData.get_all_plan_types &&
			selectedValues.plan.length === 0
		) {
			const allPlanNames = plansData.get_all_plan_types
				.filter((plan) => plan.enabled)
				.map((plan) => plan.plan_type_name);

			setSelectedValues((prevValues) => ({
				...prevValues,
				plan: allPlanNames,
			}));
		}
	}, [plansData, selectedValues.plan.length]);

	const handleSelectChange = (name: keyof SelectedValues, value: any) => {
		setSelectedValues((prevValues) => ({
			...prevValues,
			[name]: value,
		}));
	};

	const handleDateChange = (
		name: "startDate" | "endDate",
		value: Dayjs | null
	) => {
		const formattedDate = value ? value.format("YYYY-MM-DD") : "";
		setSelectedValues((prevValues) => ({
			...prevValues,
			[name]: formattedDate,
		}));
	};

	const handleApply = () => {
		dispatch(setFilterLoader(true));
		dispatch(setFilter(selectedValues));
		dispatch(setFilterLoader(false));
	};

	const locationOptions = isFetching
		? []
		: locationData &&
		  locationData.map((site, index) => ({
				id: index,
				value: site.value,
				label: site.label,
		  }));

	const locationValue =
		locationOptions &&
		locationOptions.filter((option) =>
			selectedValues.location.includes(option.value)
		);

	const temporary_translation: any = {
		"Deluxe Shine & Go": "Deluxe",
		"Premium Shield": "Premium",
		"Ultimate Total Protection": "Ultimate",
		"Classic Clean & Go": "Classic",
	};

	const planOptions = isFetchingPlan
		? []
		: plansData &&
		  plansData?.get_all_plan_types
				.filter((plan) => plan.enabled)
				.map((plan) => ({
					id: plan.plan_id,
					value: temporary_translation[plan.plan_type_name],
					label: `${plan.plan_type_name}`,
					amount: plan.amount,
				}));

	const planValue =
		planOptions &&
		planOptions.filter((option) => selectedValues.plan.includes(option.value));

	return (
		<div
			className={`header ${
				selectedValues.location.length > 0 ? "expanded" : ""
			}`}
		>
			{!checkPagesTogether && (
				<Select
					isMulti
					name="location"
					className="location-select selected-options"
					options={locationOptions}
					value={locationValue}
					onChange={(selectedOptions) => {
						const selectedValues = selectedOptions.map(
							(option: any) => option.value
						);
						handleSelectChange("location", selectedValues);
					}}
					isLoading={isFetching}
					isDisabled={isFetching || isError}
					// Fixes the z-index issues
					menuPortalTarget={document.body}
					menuPosition="fixed"
					styles={{
						menuPortal: (base) => ({...base, zIndex: 2147483647}),
					}}
				/>
			)}

			{!checkPagesTogether && (
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<DatePicker
						label="Start Date"
						className="date-picker"
						value={
							selectedValues.startDate ? dayjs(selectedValues.startDate) : null
						}
						onChange={(newValue: Dayjs | null) =>
							handleDateChange("startDate", newValue)
						}
						sx={{
							"& .MuiInputBase-root": {height: "38px"},
							"& .MuiInputLabel-root": {top: "-8px"},
							"& .MuiFormLabel-root": {top: "-8px"},
						}}
						slotProps={{
							popper: {
								sx: {zIndex: 2147483647, marginTop: "8px !important"},
							},
						}}
					/>
				</LocalizationProvider>
			)}

			{!checkPagesTogether && (
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<DatePicker
						label="End Date"
						className="date-picker"
						minDate={
							selectedValues.startDate
								? dayjs(selectedValues.startDate)
								: dayjs()
						}
						value={
							selectedValues.endDate ? dayjs(selectedValues.endDate) : null
						}
						onChange={(newValue: Dayjs | null) =>
							handleDateChange("endDate", newValue)
						}
						sx={{
							"& .MuiInputBase-root": {height: "38px"},
							"& .MuiInputLabel-root": {top: "-8px"},
							"& .MuiFormLabel-root": {top: "-8px"},
						}}
						slotProps={{
							popper: {
								sx: {zIndex: 2147483647, marginTop: "8px !important"},
							},
						}}
					/>
				</LocalizationProvider>
			)}

			{!checkPagesTogether && (
				<Select
					isMulti
					name="plan"
					className="plan-select-container"
					options={planOptions}
					value={planValue}
					onChange={(selectedOptions) => {
						const selectedValues = selectedOptions
							? selectedOptions.map((option: any) => option.value)
							: [];
						handleSelectChange("plan", selectedValues);
					}}
					isLoading={isFetchingPlan}
					isDisabled={isFetchingPlan || errorPlan}
					// Fixes the z-index issues
					menuPortalTarget={document.body}
					menuPosition="fixed"
					styles={{
						menuPortal: (base) => ({...base, zIndex: 2147483647}),
					}}
				/>
			)}

			{!checkPagesTogether && <Button onClick={handleApply}>Apply</Button>}
		</div>
	);
};

export default Header;
